import React from 'react';
import PropTypes from 'prop-types';
import { ResponsiveBar } from '@nivo/bar';
import numeral from 'numeral';

const AgeDemo = ({
  query: { data, loading },
  sortKey,
  indexKey,
  keys,
  layout,
  width,
  topTooltipLabel,
  showSize = true,
  bottomTooltipLabel
}) => {
  const ToolTip = d => {
    const { age, index } = d.data;

    return (
      <div className="tvt-timeline__tooltip">
        <div className="tvt-timeline__tooltip-data">
          <div className="tvt-timeline__tooltip-data-date">{`AGE ${age}`}</div>
          <div className="tvt-timeline__tooltip-data-label">
            {topTooltipLabel}:
            <div className="tvt-timeline__tooltip-data-followers">
              {`${numeral(index * 100).format('0.00')}%`}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const sortData = d =>
    sortKey ? d.sort((a, b) => (a[sortKey] < b[sortKey] ? 1 : -1)) : d;

  const sampleSize = d =>
    d.reduce((accum, current) => (accum += current.viewers), 0);

  const HorizontalBarComponent = ({ x, y, width, height, color, label }) => {
    return (
      <g transform={`translate(${x}, ${y})`}>
        <rect
          width={width}
          height={height}
          fill={color}
          strokeWidth="0"
          stroke="white"
        />

        <text
          x={width + 23}
          y={height / 2}
          textAnchor="middle"
          dominantBaseline="central"
          style={{
            fontSize: 12,
            pointerEvents: 'none',
            fill: '#888888'
          }}
        >
          {label}
        </text>
      </g>
    );
  };

  if (layout === 'horizontal') {
    return !loading ? (
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        {showSize && (
          <div className="tvt-chart__sample-size-num">
            {`N = ${numeral(sampleSize(data)).format(',')}`}
          </div>
        )}
        <ResponsiveBar
          data={sortData(data)}
          labelFormat={d => {
            if ( d === 0 ) {
              return null;
            }
            return (
              <tspan>{`${numeral(d * 100).format('0.00')}%`}</tspan>
            );
          }}
          barComponent={HorizontalBarComponent}
          indexBy={indexKey}
          keys={keys}
          margin={{
            top: 20,
            right: 40,
            bottom: 20,
            left: 69
          }}
          padding={0.3}
          colors="#373737"
          axisRight={null}
          axisBottom={false}
          axisLeft={{
            tickSize: 0,
            tickPadding: 15,
            tickRotation: 0
          }}
          layout="horizontal"
          enableGridX={false}
          enableGridY={false}
          maxValue="auto"
          minValue="auto"
          tooltip={ToolTip}
          theme={{
            tooltip: {
              container: {
                background: '#000'
              }
            }
          }}
          onMouseEnter={(_data, event) => {
            event.target.style.opacity = '.8';
          }}
          onMouseLeave={(_data, event) => {
            event.target.style.opacity = '1';
          }}
          width={width}
        />
      </div>
    ) : (
      <div className="tvt-time-chart__loading-state-wrapper vertical-bar-loading">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="531"
          height="276"
          viewBox="0 0 531 276"
        >
          <defs>
            <linearGradient id="9zy34c60qa" x1="0%" x2="100%" y1="50%" y2="50%">
              <stop offset="0%" stopColor="#EEEEEE" />
              <stop offset="33%" stopColor="#EEEEEE" />
              <stop offset="50%" stopColor="#f5f5f5" />
              <stop offset="67%" stopColor="#EEEEEE" />
              <stop offset="100%" stopColor="#EEEEEE" />
              <animateTransform
                attributeName="gradientTransform"
                type="translate"
                from="-1 0"
                to="1 0"
                begin="0s"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </linearGradient>
          </defs>
          <path
            fill="url(#9zy34c60qa)"
            fillRule="evenodd"
            d="M79 286v10H29v-10h50zm58 0v10H87v-10h50zm58 0v10h-50v-10h50zm58 0v10h-50v-10h50zm58 0v10h-50v-10h50zm58 0v10h-50v-10h50zm58 0v10h-50v-10h50zm58 0v10h-50v-10h50zm58 0v10h-50v-10h50zm7-266v1H21v49h8V45h50v25h471V21h1v254H20V20h530zM29 224h-8v50h8v-50zm58 0h-8v50h8v-50zm58 0h-8v50h8v-50zm58 0h-8v50h8v-50zm58 0h-8v50h8v-50zm58 0h-8v50h8v-50zm58 0h-8v50h8v-50zm173 0H427v50h8v-39h50v39h8v-23h50v23h7v-50zM29 173h-8v50h8v-50zm58 0h-8v50h8v-50zm58 0h-8v50h8v-50zm58 0h-8v50h8v-50zm347 0H253v50h8v-49h50v49h8v-31h50v31h8v-10h50v10h123v-50zM29 122h-8v50h8v-50zm58 0h-8v50h8v-50zm58 0h-8v50h8v-50zm405 0H195v50h8v-31h50v31h297v-50zM29 71h-8v50h8V71zm521 0H79v50h8V80h50v41h8v-8h50v8h355V71z"
            transform="translate(-20 -20)"
          />
        </svg>
      </div>
    );
  }

  return !loading ? (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {showSize && (
        <div className="tvt-chart__sample-size-num">
          {`N = ${numeral(sampleSize(data)).format(',')}`}
        </div>
      )}
      <ResponsiveBar
        data={sortData(data).reverse()}
        labelFormat={d => {
          if (d === 0) {
            return null;
          }
          return (
            <tspan y={-10}>{`${numeral(d * 100).format('0.00')}%`}</tspan>
          );
        }}
        indexBy={indexKey}
        keys={keys}
        margin={{
          top: 30,
          right: 20,
          bottom: 30,
          left: 20
        }}
        padding={0.3}
        colors="#373737"
        axisRight={null}
        axisBottom={{
          tickSize: 0,
          tickPadding: 0,
          tickRotation: 0
        }}
        axisLeft={null}
        enableGridX={false}
        enableGridY={false}
        maxValue="auto"
        minValue="auto"
        tooltip={ToolTip}
        theme={{
          tooltip: {
            container: {
              background: '#000'
            }
          }
        }}
        onMouseEnter={(_data, event) => {
          event.target.style.opacity = '.8';
        }}
        onMouseLeave={(_data, event) => {
          event.target.style.opacity = '1';
        }}
        width={width}
      />
    </div>
  ) : (
    <div className="tvt-time-chart__loading-state-wrapper">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="531"
        height="276"
        viewBox="0 0 531 276"
      >
        <defs>
          <linearGradient id="9zy34c60qa" x1="0%" x2="100%" y1="50%" y2="50%">
            <stop offset="0%" stopColor="#EEEEEE" />
            <stop offset="33%" stopColor="#EEEEEE" />
            <stop offset="50%" stopColor="#f5f5f5" />
            <stop offset="67%" stopColor="#EEEEEE" />
            <stop offset="100%" stopColor="#EEEEEE" />
            <animateTransform
              attributeName="gradientTransform"
              type="translate"
              from="-1 0"
              to="1 0"
              begin="0s"
              dur="1.5s"
              repeatCount="indefinite"
            />
          </linearGradient>
        </defs>
        <path
          fill="url(#9zy34c60qa)"
          fillRule="evenodd"
          d="M79 286v10H29v-10h50zm58 0v10H87v-10h50zm58 0v10h-50v-10h50zm58 0v10h-50v-10h50zm58 0v10h-50v-10h50zm58 0v10h-50v-10h50zm58 0v10h-50v-10h50zm58 0v10h-50v-10h50zm58 0v10h-50v-10h50zm7-266v1H21v49h8V45h50v25h471V21h1v254H20V20h530zM29 224h-8v50h8v-50zm58 0h-8v50h8v-50zm58 0h-8v50h8v-50zm58 0h-8v50h8v-50zm58 0h-8v50h8v-50zm58 0h-8v50h8v-50zm58 0h-8v50h8v-50zm173 0H427v50h8v-39h50v39h8v-23h50v23h7v-50zM29 173h-8v50h8v-50zm58 0h-8v50h8v-50zm58 0h-8v50h8v-50zm58 0h-8v50h8v-50zm347 0H253v50h8v-49h50v49h8v-31h50v31h8v-10h50v10h123v-50zM29 122h-8v50h8v-50zm58 0h-8v50h8v-50zm58 0h-8v50h8v-50zm405 0H195v50h8v-31h50v31h297v-50zM29 71h-8v50h8V71zm521 0H79v50h8V80h50v41h8v-8h50v8h355V71z"
          transform="translate(-20 -20)"
        />
      </svg>
    </div>
  );
};

AgeDemo.propTypes = {
  query: PropTypes.shape({
    data: PropTypes.oneOfType([
      PropTypes.shape({}),
      PropTypes.arrayOf(PropTypes.shape({}))
    ]),
    loading: PropTypes.bool
  }).isRequired,
  sortKey: PropTypes.string,
  indexKey: PropTypes.string.isRequired,
  keys: PropTypes.arrayOf(PropTypes.string).isRequired,
  layout: PropTypes.string,
  width: PropTypes.number,
  topTooltipLabel: PropTypes.string,
  bottomTooltipLabel: PropTypes.string
};

AgeDemo.defaultProps = {
  sortKey: '',
  layout: 'horizontal',
  width: 330,
  topTooltipLabel: '% Followers',
  bottomTooltipLabel: 'Followers'
};

export default AgeDemo;
